import React from 'react';
import styles from './FeatureDiscoveryWidget.module.css';
import { Lock, Unlock, CheckCircle, Trophy, Target, Users, Award, Shield } from 'lucide-react';

interface Feature {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  requirement: {
    type: 'points' | 'flag' | 'role' | 'level';
    value: number | string;
    label: string;
  };
  isUnlocked: boolean;
  progress?: number; // 0-100 for progress bar
}

interface FeatureDiscoveryWidgetProps {
  orgPoints: number;
  userFlags: string[];
  userRole: string;
}

const FeatureDiscoveryWidget: React.FC<FeatureDiscoveryWidgetProps> = ({ orgPoints, userFlags, userRole }) => {
  
  // Debug logging
  console.log('FeatureDiscoveryWidget rendered:', { orgPoints, userFlags, userRole });
  
  const getFeatures = (): Feature[] => {
    return [
      {
        id: 'missions',
        name: 'Mission Access',
        description: 'Accept and complete organization missions',
        icon: <Target size={20} />,
        requirement: {
          type: 'points',
          value: 10,
          label: 'Level 1 (10 ORG Points)'
        },
        isUnlocked: orgPoints >= 10 || userFlags.includes('CanAcceptMissions'),
        progress: orgPoints < 10 ? Math.min(100, (orgPoints / 10) * 100) : 100
      },
      {
        id: 'events',
        name: 'Event Access',
        description: 'RSVP and attend organization events',
        icon: <Users size={20} />,
        requirement: {
          type: 'flag',
          value: 'OrgMember',
          label: 'OrgMember Status'
        },
        isUnlocked: userFlags.includes('OrgMember'),
        progress: userFlags.includes('OrgMember') ? 100 : 0
      },
      {
        id: 'members-directory',
        name: 'Members Directory',
        description: 'View and connect with other members',
        icon: <Users size={20} />,
        requirement: {
          type: 'flag',
          value: 'OrgMember',
          label: 'OrgMember Status'
        },
        isUnlocked: userFlags.includes('OrgMember'),
        progress: userFlags.includes('OrgMember') ? 100 : 0
      },
      {
        id: 'level-2',
        name: 'Operator Status',
        description: 'Unlock intermediate missions, exclusive Discord role, and increased mission rewards',
        icon: <Shield size={20} />,
        requirement: {
          type: 'points',
          value: 100,
          label: 'Level 2 (100 ORG Points)'
        },
        isUnlocked: orgPoints >= 100,
        progress: orgPoints < 100 ? Math.min(100, (orgPoints / 100) * 100) : 100
      },
      {
        id: 'level-3',
        name: 'Specialist Status',
        description: 'Priority event signup, early feature access, and special Discord permissions',
        icon: <Award size={20} />,
        requirement: {
          type: 'points',
          value: 500,
          label: 'Level 3 (500 ORG Points)'
        },
        isUnlocked: orgPoints >= 500,
        progress: orgPoints < 500 ? Math.min(100, (orgPoints / 500) * 100) : 100
      },
      {
        id: 'level-4',
        name: 'Veteran Status',
        description: 'Squad lead consideration, mentor role, and 25% bonus on mission rewards',
        icon: <Trophy size={20} />,
        requirement: {
          type: 'points',
          value: 1000,
          label: 'Level 4 (1,000 ORG Points)'
        },
        isUnlocked: orgPoints >= 1000,
        progress: orgPoints < 1000 ? Math.min(100, (orgPoints / 1000) * 100) : 100
      }
    ];
  };

  const features = getFeatures();
  const lockedFeatures = features.filter(f => !f.isUnlocked);
  const unlockedFeatures = features.filter(f => f.isUnlocked);

  if (lockedFeatures.length === 0) {
    return (
      <div className={styles.widget}>
        <div className={styles.header}>
          <Unlock size={20} />
          <h3>All Core Features Unlocked!</h3>
        </div>
        <p className={styles.congratsMessage}>
          🎉 You've unlocked all core features! Keep earning ORG Points to reach higher levels and unlock exclusive perks.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <Lock size={20} />
        <h3>Feature Discovery</h3>
      </div>
      <p className={styles.subtitle}>
        Unlock new features by earning ORG Points and completing your profile
      </p>

      {lockedFeatures.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>🔒 Locked Features</div>
          <div className={styles.featureList}>
            {lockedFeatures.map(feature => (
              <div key={feature.id} className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  {feature.icon}
                </div>
                <div className={styles.featureContent}>
                  <div className={styles.featureName}>{feature.name}</div>
                  <div className={styles.featureDescription}>{feature.description}</div>
                  <div className={styles.requirement}>
                    <Lock size={14} />
                    <span>Requires: {feature.requirement.label}</span>
                  </div>
                  {feature.progress !== undefined && feature.progress < 100 && (
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill} 
                          style={{ width: `${feature.progress}%` }}
                        />
                      </div>
                      <div className={styles.progressLabel}>
                        {Math.round(feature.progress)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {unlockedFeatures.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>✅ Unlocked Features</div>
          <div className={styles.unlockedList}>
            {unlockedFeatures.map(feature => (
              <div key={feature.id} className={styles.unlockedItem}>
                <CheckCircle size={16} className={styles.checkIcon} />
                <span>{feature.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureDiscoveryWidget;
